import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBullseye,
  FaEye,
  FaHandshake,
  FaLeaf,
  FaAward,
  FaHeart,
} from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';

const About = () => {
  const values = [
    {
      icon: FaHandshake,
      title: 'Integrity',
      description: 'We conduct our business with honesty, transparency, and respect for every customer.',
    },
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'We strive for the highest standards in every cleaning job, no matter how big or small.',
    },
    {
      icon: FaLeaf,
      title: 'Sustainability',
      description: 'We use eco-friendly products and practices to protect your health and the environment.',
    },
    {
      icon: FaHeart,
      title: 'Customer Care',
      description: 'Your satisfaction is our top priority. We listen, we care, and we deliver results.',
    },
  ];

  return (
    <div>
      <Seo
        title="About The Skilled Group"
        description="Learn how The Skilled Group grew from a Brisbane carpet cleaning business into a trusted team for pest control and home cleaning services."
        canonicalPath="/about"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>About The Skilled Group</h1>
          <p>
            Brisbane's trusted cleaning professionals since 2025. Learn about our
            story, mission, and the team behind the clean.
          </p>
        </div>
      </div>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-primary-50 text-primary-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-6">
                Years of Delivering Spotless Cleans Across Brisbane
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Skilled Group was founded in 2025 with a simple belief: 
                a healthy, comfortable home requires expert care. What started as a dedicated
                team of pest management and specialized cleaning experts has grown into 
                Brisbane’s trusted name for total home protection and restoration.
              </p>
              <p className="text-gray-600 leading-relaxed font-semibold mb-4">
                Unit 6
                <br />
                376 Milton Road, Auchenflower, QLD,4066
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                While we are your go-to defense against pests—delivering fast,
                reliable eradication of everything from cockroaches and rodents
                to bed bugs—we know that true cleanliness goes deeper
                than the surface. That is why we offer a comprehensive suite of premium
                care services, including high-end apartment/unit cleaning, meticulous bond cleans,
                and deep steam cleaning for your couches, carpets, mattresses, tiles, and grout.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our team of trained professionals are bonded, 
                insured, and committed to the highest standards. 
                Whether you are dealing with an urgent pest issue,
                moving out of a rental, or looking to revitalize 
                your living spaces, we bring the same passion and
                precision to every job. That’s the Skilled Group 
                difference.
              </p>
            </div>

            <div className="space-y-6">
              {/* Mission */}
              <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaBullseye className="text-white" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-primary-500">
                    Our Mission
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  To provide Brisbane homes and businesses with professional, reliable,
                  and eco-friendly cleaning services that exceed expectations and create
                  healthier living and working environments.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-accent-50 rounded-xl p-6 border border-accent-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaEye className="text-white" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-accent-600">
                    Our Vision
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  To be Australia's most trusted and sustainable cleaning company,
                  setting the standard for quality, innovation, and environmental
                  responsibility in the cleaning industry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block bg-primary-50 text-primary-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <div
                key={value.title}
                className="bg-white rounded-xl p-6 border border-gray-100 text-center hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="text-2xl text-primary-500" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-500 py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Ready to Experience The Skilled Group Difference?
          </h2>
            <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers across Brisbane. Book your first
            clean today and see why The Skilled Group is Brisbane's most trusted cleaning service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="btn-accent inline-flex items-center justify-center gap-2"
            >
              Book Now
              <FaArrowRight className="text-sm" />
            </Link>
            <Link
              to="/contact"
              className="btn-outline border-white text-white hover:bg-white hover:text-primary-500 inline-flex items-center justify-center gap-2"
            >
              Contact Us
              <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;